import React, { useState, useEffect } from 'react';
import { Typography, Space, Table, Select, DatePicker, Input, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import reportService, { ActivityLog, ReportListParams } from '../services/reports';
import { message } from '../utils/message';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Reports: React.FC = () => {
  // 상태 관리
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [menuNames, setMenuNames] = useState<string[]>([]);

  // 필터 상태
  const [selectedMenu, setSelectedMenu] = useState<string | undefined>(undefined);
  const [selectedRole, setSelectedRole] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  // 페이지네이션 상태
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: false,
  });

  // 필터 옵션 로드
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await reportService.getReportFilters();
        setMenuNames(response.data.menuNames);
      } catch (error: any) {
        console.error('필터 옵션 로드 실패:', error);
        message.error('필터 옵션을 불러오는데 실패했습니다.');
      }
    };

    loadFilters();
  }, []);

  // 리포트 데이터 로드
  const loadReports = async (params?: ReportListParams) => {
    try {
      setLoading(true);

      const requestParams: ReportListParams = {
        page: pagination.current,
        limit: pagination.pageSize,
        menuName: selectedMenu,
        role: selectedRole,
        search: searchKeyword || undefined,
        startDate: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        endDate: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
        ...params,
      };

      const response = await reportService.getReports(requestParams);

      setLogs(response.data.logs);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.totalItems,
        current: response.data.pagination.currentPage,
      }));
    } catch (error: any) {
      console.error('리포트 로드 실패:', error);
      message.error(error.response?.data?.message || '리포트를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 초기 로드
  useEffect(() => {
    loadReports();
  }, []);

  // 필터 변경 핸들러 (변경 시 자동 검색)
  const handleMenuChange = (value: string | undefined) => {
    setSelectedMenu(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadReports({
      page: 1,
      menuName: value
    });
  };

  const handleRoleChange = (value: string | undefined) => {
    setSelectedRole(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadReports({
      page: 1,
      role: value
    });
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const newDateRange = dates || [null, null];
    setDateRange(newDateRange);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadReports({
      page: 1,
      startDate: newDateRange[0] ? newDateRange[0].format('YYYY-MM-DD') : undefined,
      endDate: newDateRange[1] ? newDateRange[1].format('YYYY-MM-DD') : undefined
    });
  };

  // 검색 핸들러
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadReports({
      page: 1,
      search: e.target.value || undefined
    });
  };

  // 테이블 변경 핸들러 (페이지네이션, 정렬 등)
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || 20,
    }));
    loadReports({
      page: newPagination.current,
      limit: newPagination.pageSize
    });
  };


  // 테이블 컬럼 정의
  const columns: ColumnsType<ActivityLog> = [
    {
      title: '메뉴명',
      dataIndex: 'menuName',
      key: 'menuName',
      width: 150,
      render: (text: string) => (
        <Text style={{ color: '#ffffff' }}>{text}</Text>
      ),
    },
    {
      title: '내용',
      dataIndex: 'content',
      key: 'content',
      width: 300,
      render: (text: string) => (
        <Text style={{ color: '#ffffff' }}>{text}</Text>
      ),
    },
    {
      title: '관리자명',
      dataIndex: 'username',
      key: 'username',
      width: 150,
      render: (text: string, record: any) => {
        // User 모델에서 name 필드를 우선 표시, 없으면 username 표시
        const displayName = record.user?.name || text;
        return <Text style={{ color: '#ffffff' }}>{displayName}</Text>;
      },
    },
    {
      title: '일시',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text: string) => (
        <Text style={{ color: '#ffffff' }}>
          {dayjs(text).format('YYYY.MM.DD HH:mm')}
        </Text>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 페이지 제목 */}
        <div style={{ margin: '0 0 32px 0' }}>
          <Title level={2} style={{ margin: '0', color: '#ffffff' }}>
            리포트
          </Title>
        </div>

        {/* 필터 영역 */}
        <div style={{ marginBottom: '24px' }}>
          {/* 필터 행 */}
          <div style={{
            display: 'flex',
            // gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
            alignItems: 'flex-start',
            marginBottom: '24px'
          }}>
            {/* 메뉴 필터 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '300px', minWidth: '250px' }}>
              <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>메뉴</Text>
              <Select
                value={selectedMenu}
                onChange={handleMenuChange}
                placeholder="전체"
                allowClear
                style={{ width: '100%' }}
                styles={{
                  popup: {
                    root: { background: '#1f1f1f' }
                  }
                }}
              >
                {menuNames.map(name => (
                  <Option key={name} value={name}>{name}</Option>
                ))}
              </Select>
            </div>

            {/* 권한 필터 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '300px', minWidth: '250px' }}>
              <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>권한</Text>
              <Select
                value={selectedRole}
                onChange={handleRoleChange}
                placeholder="전체"
                allowClear
                style={{ width: '100%' }}
                styles={{
                  popup: {
                    root: { background: '#1f1f1f' }
                  }
                }}
              >
                <Option value="admin">Admin</Option>
                <Option value="super_admin">Super Admin</Option>
              </Select>
            </div>

            {/* 기간 필터 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '350px', minWidth: '300px' }}>
              <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>기간</Text>
              <RangePicker
                value={dateRange as any}
                onChange={handleDateRangeChange}
                placeholder={['Start Date', 'End Date']}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* 가로 구분선 */}
          <div style={{
            borderTop: '1px solid #303030',
            marginBottom: '24px'
          }} />

          {/* 검색 행 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            {/* 결과 카운트 */}
            <Text style={{ color: '#a6a6a6' }}>
              총 {pagination.total}건
            </Text>

            {/* 검색 입력 */}
            <Input
              placeholder="검색"
              suffix={<SearchOutlined style={{ color: '#a6a6a6' }} />}
              style={{
                width: 400,
                height: 40,
                borderRadius: 8,
                background: '#2a2a2a',
                border: '1px solid #404040',
                color: '#ffffff'
              }}
              onChange={handleSearch}
              value={searchKeyword}
              allowClear
            />
          </div>
        </div>

        {/* 테이블 */}
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          pagination={{
            ...pagination,
            style: { textAlign: 'right' }
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          style={{
            background: '#1f1f1f',
          }}
        />
      </Space>
    </Spin>
  );
};

export default Reports;